import React, { useState } from "react";
import { Box, Paper, TextField, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { OTPType } from "./types/otpVerification";
import AppButton from "../AppButton";

export interface OtpVerificationProps {
  otpType: OTPType;
  onBackToLogin: () => void;
  handleSubmitOtp: (otp: string) => void;
}

const OtpVerification: React.FC<OtpVerificationProps> = ({
  otpType,
  onBackToLogin,
  handleSubmitOtp
}) => {
  const { t } = useKivunovaTranslation();
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [errorKey, setErrorKey] = useState<"incomplete" | null>(null);

  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setErrorKey(null);

      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      } else if (!value && index > 0) {
        const prevInput = document.getElementById(`otp-${index - 1}`);
        prevInput?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length === 6) {
      setErrorKey(null);
      handleSubmitOtp(otpValue);
    } else {
      setErrorKey("incomplete");
    }
  };

  const intro =
    otpType === "phone"
      ? t(
          "otp-verification-intro-phone",
          "We've sent a 6-digit verification code to your mobile number."
        )
      : t(
          "otp-verification-intro-email",
          "We've sent a 6-digit verification code to your email address."
        );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "background.default",
        p: 2
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{ width: "100%", maxWidth: 400 }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            textAlign: "center",
            p: 4,
            borderRadius: { xs: 2, sm: 3 },
            boxShadow:
              "0 4px 24px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper"
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            mb={1}
            color="primary.main"
          >
            {t("otp-verification-title", "OTP verification")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {intro}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Box display="flex" justifyContent="center" gap={1} mb={2}>
              {otp.map((value, index) => (
                <TextField
                  key={index}
                  id={`otp-${index}`}
                  value={value}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      width: "44px",
                      height: "44px",
                      borderRadius: "12px"
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": { borderRadius: 2 },
                      "&:hover fieldset": { borderColor: "primary.light" },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                        boxShadow: "0 0 0 2px rgba(111, 73, 201, 0.2)"
                      }
                    }
                  }}
                />
              ))}
            </Box>

            {errorKey === "incomplete" && (
              <Typography variant="body2" color="error" mb={2}>
                {t(
                  "otp-verification-error-incomplete",
                  "Please fill in all 6 digits before submitting."
                )}
              </Typography>
            )}

            <AppButton
              type="submit"
              variant="primary"
              fullWidth
              sx={{ py: 1.25, borderRadius: 2 }}
            >
              {t("otp-verification-submit", "Submit")}
            </AppButton>
          </form>

          <AppButton variant="text" onClick={onBackToLogin} sx={{ mt: 3 }}>
            {t("otp-verification-back-to-login", "Back to login")}
          </AppButton>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default OtpVerification;
